import {
  type Realm,
  Auth as STAuth,
  User as STUser,
  convertBrowserLocaleToLanguageRegion,
  createAuthSyncRequest,
  createRealmWithNames,
} from "@storytell/cp-client";
import type { Named } from "@storytell/logger";
import type { Auth as FBAuth, User as FBUser } from "firebase/auth";

// Constants for guest user photo URLs
const GUEST_PHOTO_URLS = {
  DEFAULT_GUEST_PHOTO_REL_PATH: "/images/guest-avatar.png",
  DEFAULT_GUEST_PHOTO_FQL_URL: "https://storytell.ai/images/guest-avatar.png",
  DEFAULT_GUEST_PHOTO_FILENAME: "guest-avatar.png",
} as const;

// Checks if an email address belongs to a guest user
// Returns true if the email ends with @guest.storytell.ai
export const isGuestEmail = (email: string): boolean => email.endsWith("@guest.storytell.ai");

// Prepares a sync request for user data synchronization
//
// Returns a SyncRequest object containing user data for synchronization
export const prepareSyncRequest = (
  user: FBUser,
  deviceId: string,
  locale: string,
  userAgent: string,
): STAuth.SyncRequest => {
  return createAuthSyncRequest({
    workflowVariant: STAuth.SyncWorkflowVariant.WEB_APP,
    isEmailVerified: false,
    email: user.email || "guest@guest.storytell.ai",
    displayName: user?.displayName || "Guest", // Todo: i18n default name
    photoUrl: user.photoURL || GUEST_PHOTO_URLS.DEFAULT_GUEST_PHOTO_FQL_URL,
    userAgent,
    languageRegion: convertBrowserLocaleToLanguageRegion(locale),
    deviceId,
  });
};

// Creates a token refresher utility for Firebase authentication
//
// Returns an object containing refresh and schedule methods
export const createTokenRefresher = (auth: FBAuth, setToken: (token: string) => void, logger: Named) => {
  let schedule: NodeJS.Timeout | null = null;

  // Refreshes the Firebase authentication token
  const refresh = async (): Promise<void> => {
    if (!auth.currentUser) {
      logger.warn("No user to refresh token for");
      return;
    }
    logger.info("Refreshing token");
    const token = await auth.currentUser.getIdToken(true);
    setToken(token);
    schedule = null;
  };

  return {
    refresh,
    // Schedules periodic token refresh
    // seconds: Interval in seconds between token refreshes
    schedule: (seconds: number): void => {
      schedule = setInterval(refresh, 1000 * seconds);
    },
  };
};

// emptyRealm returns a default realm object with empty values.
export const emptyRealm = (): Realm.RealmWithNames => {
  return createRealmWithNames({
    tenantId: "",
    tenantName: "",
    organizationId: "",
    organizationName: "",
    workspaceId: "",
    workspaceName: "",
    projectId: undefined,
    projectName: undefined,
  });
};

// Checks if the user is currently authenticated and registered.
// This state indicates the user has completed both registration and email verification.
export const isLifecycleRegistered = (value: string | STUser.UserStatus) => value === STUser.UserStatus.REGISTERED;

// Checks if the user is currently in guest mode.
// Guest users have limited access and functionality compared to registered users.
export const isLifecycleGuest = (value: string | STUser.UserStatus) => value === STUser.UserStatus.GUEST;

// Checks if the authentication state is currently loading.
// This state is typically shown during initial load or during authentication processes.
export const isLifecycleLoading = (value: string | STUser.UserStatus) => value === "loading";

// Checks if there is an authentication error.
// When true, the error message can be accessed from the store's error property.
export const isLifecycleError = (value: string | STUser.UserStatus) => value === "error";

// Checks if the user has requested account deletion.
// This state is used to handle the account deletion workflow.
export const isLifecycleDeleteRequested = (value: string | STUser.UserStatus) =>
  value === STUser.UserStatus.DELETE_REQUESTED;

// Checks if the user has been marked for abuse.
// This state is used to restrict access for users who have violated terms of service.
export const isLifecycleMarkedForAbuse = (value: string | STUser.UserStatus) =>
  value === STUser.UserStatus.MARKED_FOR_ABUSE;

// Checks if the user's email verification is pending.
// Users in this state may have limited access until verification is complete.
export const isLifecyclePendingVerification = (value: string | STUser.UserStatus) =>
  value === STUser.UserStatus.PENDING_VERIFICATION;
